import PropTypes from 'prop-types';
import React, { Component } from 'react';
import b from 'b_';
import { observer, PropTypes as MobxPropTypes } from 'mobx-react';
import BulkEditorStore, { SPLIT_ROW } from '../../stores/BulkEditorStore';
import Global from '../../Components/Globals';
import './BulkEditor.css';

const block = b.lock('BulkEditor');
const MEASURE_TEXT_CHAR = '0';
// every pixel represented by combination of 4 (red, green,blue, alpha)
const PIXEL_SIZE = 4;
const RATIO_FOR_ZOOM = 1.2;
const MIN_ROWS_SIZE = 3;
const PIXEL_ROW_DEVIATION = 1;
export default class BulkEditor extends Component {
    static propTypes = {
        search: PropTypes.string,
        urlPatterns: MobxPropTypes.arrayOrObservableArray,
        width: PropTypes.number,
        height: PropTypes.number,
        searchRow: PropTypes.number,
        updateSearchMarks: PropTypes.func,
        setWasFocusedInLastDraw: PropTypes.func,
        updateNextSearchByPos: PropTypes.func,
        setMarginLeft: PropTypes.func,
        setPrevUrlPatterns: PropTypes.func,
        updateUrlBulkInfo: PropTypes.func,
        searchIndexInRow: PropTypes.number,
        fontSize: PropTypes.number,
        minWidth: PropTypes.number,
        caseSensitiveSearch: PropTypes.bool,
        minHeight: PropTypes.number,
        marginLeft: PropTypes.number,
        numbersColor: PropTypes.string,
        lineHeight: PropTypes.number,
        fontFamily: PropTypes.string,
        isValid: PropTypes.func,
        setUrlPatterns: PropTypes.func,
        lastGoToUpdate: PropTypes.number,
        lastSearchFocus: PropTypes.number,
        top: PropTypes.number,
        getStringForSearch: PropTypes.func,
        validator: PropTypes.func,
        validators: MobxPropTypes.arrayOrObservableArray,
        goTo: PropTypes.func,
        lineHighlight: PropTypes.string,
        placeholder: PropTypes.string,
        invalidCount: PropTypes.number,
        needsWarning: PropTypes.func,
        warningBackground: PropTypes.string,
        numbersWarningColor: PropTypes.string,
        hasWarnings: PropTypes.func,
    };

    static defaultProps = {
        fontSize: 15,
        lineHeight: 1.4,
        numbersColor: 'rgba(66, 66, 66, .87)',
        lineHighlight: 'rgba(183, 28, 28, .14)',
    };

    updateSizeMode = false;
    paddingNumber = 7;
    sizeOfLastLineNumber = 2;
    extraLinesForResize = 150;
    borderSize = 2;
    paddingNumberRight = 4;
    isFocused = false;
    textAreasRef = React.createRef();
    canvasRef = React.createRef();
    fontFamily;
    fontHeight;
    paddingTextArea = 2;
    touched = false;
    destFromTop = 0;
    dragSymbolSize = 14 + this.paddingTextArea;

    constructor(props) {
        super(props);
        props.setMarginLeft(this.calculateMarginLeft(this.sizeOfLastLineNumber, props.fontSize, props.fontFamily));
        this.rowSize = props.lineHeight * props.fontSize;
        props.setPrevUrlPatterns(props.urlPatterns || []);
    }

    componentDidMount() {
        this.init();
    }

    componentWillUnmount() {
        this.unmountComponent = true;
        this.props.setPrevUrlPatterns();
    }

    componentWillUpdate(nextProps) {
        if (nextProps.search !== this.props.search && nextProps.search !== '' &&
            this.isNeedToUpdateFocusedSearch(nextProps.search, nextProps.urlPatterns)) {
            this.props.updateSearchMarks(this.getFirstLine());
        }
        if (nextProps.marginLeft !== this.props.marginLeft && this.textAreasRef &&
            this.textAreasRef.current && this.textAreasRef.current.style &&
            this.textAreasRef.current.style.width) {
            this.textAreasRef.current.style.width = `${parseInt(this.textAreasRef.current.style.width, 10) +
            (this.props.marginLeft - nextProps.marginLeft)}px`;
        }
    }

    componentDidUpdate(prevProps) {
        // in case of scroll, the numbers may not be in the same size.
        if (prevProps.marginLeft !== this.props.marginLeft) {
            this.updateCanvasSize();
            this.textAreaPrevWidth = this.textAreasRef.current.style.width;
        }
        if (prevProps.width !== this.props.width) {
            this.width = this.props.width;
        }
        if (this.props.lastSearchFocus !== prevProps.lastSearchFocus && ((Number.isInteger(this.props.searchRow)
            && Number.isInteger(this.props.searchIndexInRow))
        )) {
            this.focusSearch();
        }
        // new event of go to line.
        if (prevProps.lastGoToUpdate !== this.props.lastGoToUpdate) {
            const caretPosition = this.props.urlPatterns.slice(0, Math.max(0,
                this.props.top - 1)).join(SPLIT_ROW).length;
            this.setCaretPosition(caretPosition === 0 ? 0 : caretPosition + 1);
            if (this.textAreasRef.current.scrollLeft) {
                this.textAreasRef.current.scrollLeft = 0;
            }
            this.textAreasRef.current.scrollTop = Math.min(this.getRowSize() * (this.props.top - 1)
                - this.destFromTop, this.textAreasRef.current.scrollHeight);
            this.destFromTop = 0;
        }
        this.drawCanvas();
    }

    init() {
        this.ctx = this.canvasRef.current.getContext('2d');
        this.fontFamily = window.getComputedStyle(this.textAreasRef.current)
            .getPropertyValue('font-family');
        this.ctx.font = this.props.fontSize + `px ${this.props.fontFamily ? this.props.fontFamily :
            this.fontFamily}`;
        this.textAreasRef.current.value = this.getTextareaText();
        this.drawCanvas();
        this.textAreaPrevWidth = this.textAreasRef.current.style.width;
        this.textAreaPrevHeight = this.textAreasRef.current.style.height;
        this.curDevicePixelRatio = window.devicePixelRatio || 1;
        this.observeResize();
        this.updateCanvasSize();
        this.props.updateUrlBulkInfo();
    }

    calculateMarginLeft(numberingCharacterCounter, fontSize, fontFamily) {
        let ctx = this.ctx;
        if (!ctx) {
            const canvas = document.createElement('canvas');
            ctx = canvas.getContext('2d');
            if (fontFamily) {
                ctx.font = fontSize + `px ${fontFamily}`;
            } else {
                // In case we are using the default font
                ctx.font = ctx.font.replace(/\d+px/, `${fontSize}px`);
            }
        }
        // calculate the width of a number according to our font size and font
        return Math.ceil(ctx.measureText(MEASURE_TEXT_CHAR.repeat(numberingCharacterCounter)).width +
            this.paddingNumber * 2);
    }

    updateFontHeight() {
        // calculate the width of '0'
        const w = Math.ceil(this.ctx.measureText(MEASURE_TEXT_CHAR).width);
        // reset the part of the canvas that we want to measure the height of numbers
        this.ctx.clearRect(0, 0, w, this.props.fontSize);
        this.ctx.fillStyle = this.props.numbersColor;
        this.ctx.fillText(MEASURE_TEXT_CHAR, 0, this.props.fontSize);
        const imageData = this.ctx.getImageData(0, 0, w, this.props.fontSize);
        let indexTop = 0;
        // every pixel represent with 4 cell due to that we can jump 4 cell
        while (imageData.data[indexTop] === 0) {
            indexTop += PIXEL_SIZE;
        }
        let indexBottom = imageData.data.length;
        while (imageData.data[indexBottom] === 0) {
            indexBottom -= PIXEL_SIZE;
        }
        const row = (PIXEL_SIZE * w);
        this.fontHeight = Math.floor(indexBottom / row) - Math.floor(indexTop / row);
        this.ctx.clearRect(0, 0, w, this.props.fontSize);
    }

    observeResize = () => {
        let needToUpdateCanvasSize = false;
        if (!this.unmountComponent && this.textAreasRef && this.textAreasRef.current) {
            if (this.textAreasRef.current.style.width !== this.textAreaPrevWidth ||
                this.textAreasRef.current.style.height !== this.textAreaPrevHeight) {
                this.textAreaPrevWidth = this.textAreasRef.current.style.width;
                this.textAreaPrevHeight = this.textAreasRef.current.style.height;
                this.startToResizeEvent();
            }
            if (this.textAreasRef.current.scrollHeight !== this.scrollHeightPrev &&
                this.rowSize * this.props.urlPatterns.length >
                RATIO_FOR_ZOOM * parseInt(this.textAreasRef.current.style.height, 10)) {
                if (Math.abs(this.textAreasRef.current.scrollHeight / this.props.urlPatterns.length
                    - (this.props.lineHeight * this.props.fontSize)) < PIXEL_ROW_DEVIATION) {
                    this.scrollHeightPrev = this.textAreasRef.current.scrollHeight;
                    this.updateRowSize();
                    needToUpdateCanvasSize = true;
                }
            }
            // handle zoom event or in case that user is changing screen (dragging browser to anther screen)
            if (this.curDevicePixelRatio !== window.devicePixelRatio ||
            this.prevDocumentClientWidth !== document.documentElement.clientWidth ||
                this.prevDocumentClientHeight !== document.documentElement.clientHeight) {
                // client width should change at zoom in/out event
                this.prevDocumentClientWidth = document.documentElement.clientWidth;
                this.prevDocumentClientHeight = document.documentElement.clientHeight;
                needToUpdateCanvasSize = true;
            }
            if (needToUpdateCanvasSize) {
                this.updateCanvasSize();
            }
            setTimeout(this.observeResize, this.updateSizeMode ? 5 : 100);
        }
    }

    updateRowSize(size = undefined, scrollHeight = undefined) {
        let newRowSize = parseInt(scrollHeight || this.textAreasRef.current.scrollHeight, 10) /
            (size || this.props.urlPatterns.length);
        if (this.rowSize !== newRowSize) {
            // prevent update in case that the result is not calculated with the correct parameter.
            // For example: scrollHeight is not updated in the temp text area.
            if (newRowSize >= MIN_ROWS_SIZE) {
                this.rowSize = newRowSize;
            } else if (size && parseInt(this.textAreasRef.current.scrollHeight, 10) >
                parseInt(this.textAreasRef.current.style.height, 10)) {
                // In case we failed to calculate, we can do it by the original text area
                newRowSize = parseInt(this.textAreasRef.current.scrollHeight, 10) /
                    this.props.urlPatterns.length;
                if (newRowSize >= MIN_ROWS_SIZE) {
                    this.rowSize = newRowSize;
                }
            }
        }
    }

    isNeedToLookForNextSearch(search, urlPatterns) {
        return this.props.searchRow === null || this.props.searchRow >= urlPatterns.length ||
            this.props.searchIndexInRow === null ||
            this.props.getStringForSearch(urlPatterns[this.props.searchRow].substring(this.props.searchIndexInRow))
                .indexOf(this.props.getStringForSearch(search)) !== 0;
    }

    isNeedToUpdateFocusedSearch(search, urlPatterns) {
        return this.props.searchRow === null || this.props.searchRow >= urlPatterns.length ||
            this.props.searchIndexInRow === null || this.search !== '';
    }

    getRowSize() {
        return this.rowSize;
    }

    isTextAreaEmpty(urlPatterns = undefined) {
        const urlPatternsForCheck = urlPatterns || this.props.urlPatterns;
        if (!urlPatternsForCheck || urlPatternsForCheck.length === 0) {
            return true;
        }
        return urlPatternsForCheck.length === 1 && (!urlPatternsForCheck[0] || urlPatternsForCheck[0] === '');
    }

    drawCanvas(urlPatterns = undefined) {
        const firstLine = this.getFirstLine();
        const lastLine = Math.min(this.getLastLine() + 1, urlPatterns ? urlPatterns.length :
            this.props.urlPatterns.length);
        this.ctx.clearRect(0, 0, this.width, this.height);
        this.ctx.font = this.props.fontSize + `px ${this.props.fontFamily ? this.props.fontFamily : this.fontFamily}`;
        if (!this.fontHeight) {
            this.updateFontHeight();
        }
        if (!this.isTextAreaEmpty()) {
            for (let i = firstLine; i < lastLine; i += 1) {
                const invalidRow = this.isInvalidRow(i, urlPatterns);

                if (invalidRow) {
                    this.drawLine(i);
                } else if (this.props.needsWarning(urlPatterns?.[i] || this.props.urlPatterns[i])) {
                    this.drawWarningLine(i);
                }
                if (this.props.search && this.props.search.length > 0) {
                    const currentSearchInLine = !this.isFocused && this.props && this.props.searchRow === i;
                    this.drawSearchHighlight(i, urlPatterns ? urlPatterns[i] : this.props.urlPatterns[i],
                        currentSearchInLine);
                }
                this.drawNumbering(i, invalidRow);
            }
        }
        this.clearPadding();
        if (this.isFocused || !this.props.isValid()) {
            let color;

            if (!this.props.isValid()) {
                color = Global.redHover;
            } else if (this.props.hasWarnings()) {
                color = this.props.warningBackground;
            } else {
                color = Global.blueHover;
            }
            this.drawBorder(this.borderSize, color);
        } else {
            this.drawBorder(1, 'rgba(0, 0, 0, 0.24)');
        }
        this.props.setWasFocusedInLastDraw(this.isFocused);
    }

    clearPadding() {
        this.ctx.clearRect(0, 0, this.width, this.paddingTextArea);
        this.ctx.clearRect(0, this.height - this.paddingTextArea, this.width, this.paddingTextArea);
    }

    drawBorder(borderSize, color) {
        this.ctx.fillStyle = color;
        this.ctx.fillRect(0, 0, this.width, borderSize);
        this.ctx.fillRect(0, 0, borderSize, this.height);
        this.ctx.fillRect(0, this.height - borderSize, this.width, borderSize);
        this.ctx.fillRect(this.width - borderSize, 0, borderSize, this.height);
    }

    handleOnChange = (event) => {
        if (this.updateSizeMode) {
            this.finishUpdateSizeMode();
            return;
        }
        const urlPatterns = event.target.value.split(SPLIT_ROW).map((p) => p.trim());
        if (this.isNeedToLookForNextSearch(this.props.search, urlPatterns)) {
            this.props.updateNextSearchByPos(this.textAreasRef.current.selectionStart, urlPatterns);
        }
        this.props.setUrlPatterns(urlPatterns);
        this.updateWidthOfNumbers();
        this.drawCanvas(urlPatterns);
    }

    focusSearch() {
        if (this.props.searchRow !== null) {
            const linesInCanvas = this.getLastLine() - this.getFirstLine();
            const newTopLine = Math.max(this.props.searchRow - Math.round(linesInCanvas / 3), 0);
            this.textAreasRef.current.scrollTop = Math.min(this.getRowSize() * (newTopLine),
                this.textAreasRef.current.scrollHeight);
            const prefixWidth = this.ctx.measureText(this.props.urlPatterns[this.props.searchRow].substring(0,
                this.props.searchIndexInRow)).width;
            const searchWidth = this.ctx.measureText(this.props.search).width;
            if (prefixWidth + searchWidth > (this.width - this.getMarginLeft())) {
                this.textAreasRef.current.scrollLeft = Math.min(this.textAreasRef.current.scrollWidth,
                    Math.round(prefixWidth));
            } else {
                this.textAreasRef.current.scrollLeft = 0;
            }
        }
    }

    handleOnClickEvent= (e) => {
        if (!this.touched) {
            this.touched = true;
            const rect = e.target.getBoundingClientRect();
            this.destFromTop = e.clientY - rect.top;
            const line = 1 + (Math.floor((Math.max(this.textAreasRef.current.scrollTop, 0)
                + this.destFromTop) / this.getRowSize()));
            this.props.goTo(line);
        }
        this.textAreasRef.current.focus();
    }

    handleOnFocusEvent = () => {
        this.isFocused = true;
        this.drawCanvas();
        if (!this.touched) {
            this.touched = true;
            this.props.goTo(1);
        }
    }

    handleOnBlurEvent = () => {
        this.isFocused = false;
        this.drawCanvas();
    }

    updateWidthOfNumbers() {
        if (this.sizeOfLastLineNumber !== `${this.getLastLine()}`.length) {
            this.sizeOfLastLineNumber = `${this.getLastLine()}`.length;
            this.props.setMarginLeft(this.calculateMarginLeft(this.sizeOfLastLineNumber,
                this.props.fontSize, this.props.fontFamily));
        }
    }

    finishUpdateSizeMode() {
        clearInterval(this.resizeInterval);
        this.updateSizeMode = false;
        this.textAreasRef.current.value = this.getTextareaText();
        this.updateCanvasSize();
        this.textAreasRef.current.style.overflow = 'auto';
        this.forceUpdate();
        this.drawCanvas();
    }

    getRowsForRender() {
        const firstRow = this.getFirstLine();
        return this.updateSizeMode ?
            this.getOnlyPartOfTextArea().map((cur, index) => (index < firstRow ? '' : cur)) : this.props.urlPatterns;
    }

    drawSearchHighlight(rowIndex, line, currentSearchInLine) {
        const y = this.getRowSize() * rowIndex - this.textAreasRef.current.scrollTop + this.paddingTextArea;
        let offsetX = this.getMarginLeft();
        let charsCounter = 0;
        let searchWidth;
        const left = this.textAreasRef.current.scrollLeft;
        while (line && this.props.getStringForSearch(line)
            .indexOf(this.props.getStringForSearch(this.props.search)) > -1) {
            const index = this.props.getStringForSearch(line).indexOf(this.props.getStringForSearch(this.props.search));
            const l = line.substring(0, index);
            searchWidth = this.ctx.measureText(line.substring(index, index + this.props.search.length)).width;
            offsetX += this.ctx.measureText(l).width;
            charsCounter += l.length;
            this.ctx.fillStyle = currentSearchInLine &&
            this.props.searchIndexInRow === charsCounter ? '#FF9800' : '#ffff00';
            const paddingLeft = this.getMarginLeft() - this.paddingNumberRight;
            const width = searchWidth + Math.min(0, offsetX - (left + paddingLeft));
            if (width > 0) {
                this.ctx.fillRect(Math.max(paddingLeft, Math.floor(offsetX) - left), Math.max(0, y),
                    Math.ceil(width), this.getRowSize() + Math.min(0, y));
            }
            if (index + this.props.search.length < line.length) {
                line = line.substring(index + this.props.search.length, line.length);
                offsetX += searchWidth;
                charsCounter += this.props.search.length;
            } else {
                return;
            }
        }
    }

    getTextareaText() {
        const urlPatterns = this.getRowsForRender();
        return urlPatterns.join(SPLIT_ROW);
    }

    getOnlyPartOfTextArea() {
        const last = this.getFirstLine() + this.extraLinesForResize;
        return this.props.urlPatterns.slice(0, Math.min(last, this.props.urlPatterns.length));
    }

    startToResizeEvent() {
        this.lastResize = new Date().getTime();
        if (!this.updateSizeMode) {
            // TODO to disbale textArea overflow without disable resize in FF
            this.updateSizeMode = true;
            this.textAreasRef.current.value = this.getTextareaText();
            this.forceUpdate();
            this.resizeInterval = setInterval(() => {
                if (new Date().getTime() - this.lastResize > 1000) {
                    if (this.updateSizeMode) {
                        this.finishUpdateSizeMode();
                    }
                }
            }, 500);
        }
        this.updateCanvasSize();
    }

    updateCanvasSize() {
        if (this.textAreasRef && this.textAreasRef.current && this.textAreasRef.current.style &&
            this.textAreasRef.current.style.width) {
            this.updateHiDPICanvas(parseInt(this.textAreasRef.current.style.width, 10) + this.paddingTextArea
                + this.getMarginLeft(),
            Math.max(this.props.minHeight, parseInt(this.textAreasRef.current.style.height, 10))
                + this.paddingTextArea * 2);
        }
        this.drawCanvas();
    }

    // fixing canvas blur https://medium.com/wdstack/fixing-html5-2d-canvas-blur-8ebe27db07da
    updateHiDPICanvas(w, h) {
        this.width = w;
        this.height = h;
        const dpr = window.devicePixelRatio || 1;
        this.canvasRef.current.style.width = `${w}px`;
        this.canvasRef.current.style.height = `${h}px`;
        const rect = this.canvasRef.current.getBoundingClientRect();
        this.canvasRef.current.width = rect.width * dpr;
        this.canvasRef.current.height = rect.height * dpr;
        this.ctx.scale(dpr, dpr);
    }

    handleOnMouseUpEvent = () => {
        if (this.updateSizeMode) {
            this.finishUpdateSizeMode();
        }
    }

    handleOnScrollEvent = () => {
        this.updateWidthOfNumbers();
        this.drawCanvas();
        if (this.updateSizeMode) {
            this.finishUpdateSizeMode();
        }
    }

    drawWarningLine = (index) => {
        this.ctx.fillStyle = this.props.warningBackground;
        this.drawColoredLine(index, this.props.numbersWarningColor);
    }

    drawLine(index) {
        this.ctx.fillStyle = this.props.lineHighlight;
        this.drawColoredLine(index);
    }

    drawColoredLine(index, fillStyle = Global.redHover) {
        const y = this.getRowSize() * index - this.textAreasRef.current.scrollTop + this.paddingTextArea;
        const x = this.getMarginLeft() - this.paddingNumberRight;
        this.ctx.fillRect(x, Math.max(0, y), this.width - x, this.getRowSize() + Math.min(0, y));
        this.ctx.fillStyle = fillStyle;
        this.ctx.fillRect(0, Math.max(0, y), x, this.getRowSize() + Math.min(0, y));
    }

    drawNumbering(index, invalid) {
        this.ctx.fillStyle = invalid ? Global.textColorWhite : this.props.numbersColor;
        const y = (this.getRowSize() * (index + 1)) - this.textAreasRef.current.scrollTop + this.paddingTextArea;
        this.ctx.fillText(`${index + 1}`, this.getMarginLeft() -
            this.ctx.measureText(`${index + 1}`).width - this.paddingNumber,
        y - ((this.getRowSize() - this.fontHeight) / 2));
    }

    getFirstLine() {
        return Math.floor(Math.max(this.textAreasRef.current ? this.textAreasRef.current.scrollTop : 0, 0) /
            this.getRowSize());
    }

    getLastLine() {
        const height = parseInt(this.textAreasRef.current.style.height, 10);
        const numberOfRow = Math.floor(height / this.getRowSize()) + 1;
        return this.getFirstLine() + numberOfRow;
    }

    setCaretPosition(pos) {
        // Modern browsers
        if (this.textAreasRef.current.setSelectionRange) {
            this.textAreasRef.current.focus();
            this.textAreasRef.current.setSelectionRange(pos, pos);
        }
    }

    isInvalidRow(index, urlPatterns = undefined) {
        const { validator, validators } = this.props;
        const rowsForCheck = urlPatterns || this.props.urlPatterns;
        return index < rowsForCheck.length && rowsForCheck[index] !== '' &&
            ((validator && !validator(rowsForCheck[index])) ||
                (validators && validators.some((currentValidator) => !currentValidator(rowsForCheck[index]))));
    }

    getMarginLeft() {
        return this.props.marginLeft;
    }

    handleOnMouseDownEvent = (e) => {
        this.touched = true;
        const rect = e.target.getBoundingClientRect();
        const inHorizontalBoundary = this.dragSymbolSize >= rect.right - e.clientX;
        const inVerticalBoundary = this.dragSymbolSize >= rect.bottom - e.clientY;
        const inDragBoundaries = inHorizontalBoundary && inVerticalBoundary;
        if (inDragBoundaries) {
            this.startToResizeEvent();
        } else if (this.updateSizeMode) {
            this.finishUpdateSizeMode();
        }
    }

    render() {
        return (<div className={block('Wrapper')} onBlur={this.handleOnBlurEvent}>
            <canvas
                ref={this.canvasRef}
                className={block('Canvas')}
                width={`${this.props.width}px`}
                height={`${this.props.height}px`}
                onClick={this.handleOnClickEvent}
            />
            <textarea
                spellCheck="false"
                wrap="off"
                ref={this.textAreasRef}
                className={block('TextArea')}
                style={{
                    width: `${(this.props.width) - (this.props.marginLeft ||
                        this.calculateMarginLeft(this.sizeOfLastLineNumber, this.props.fontSize,
                            this.props.fontFamily))
                    - this.paddingTextArea}px`,
                    height: `${(this.height || this.props.height) - this.paddingTextArea * 2}px`,
                    top: `${this.paddingTextArea}px`,
                    margin: `${this.paddingTextArea}px ${this.paddingTextArea}px ${this.paddingTextArea}px
                ${this.getMarginLeft()}px`,
                    minWidth: `${this.props.minWidth - this.getMarginLeft() - this.paddingTextArea}px`,
                    minHeight: `${this.props.minHeight}px`,
                    fontSize: this.props.fontSize + 'px',
                    lineHeight: this.props.lineHeight,
                }}
                placeholder={this.props.placeholder}
                onMouseDown={this.handleOnMouseDownEvent}
                onMouseUp={this.handleOnMouseUpEvent}
                onScroll={this.handleOnScrollEvent}
                onChange={this.handleOnChange}
                onFocus={this.handleOnFocusEvent}
            />
        </div>);
    }
}

export const ObservableBulkEditor =
    observer(({ store, ...props }) => <BulkEditor
        validator={store.validator}
        validators={store.validators}
        urlPatterns={store.urlPatterns}
        setUrlPatterns={store.setUrlPatterns}
        updateSearchMarks={store.updateSearchMarks}
        searchIndexInRow={store.searchIndexInRow}
        searchRow={store.searchRow}
        updateNextSearchByPos={store.updateNextSearchByPos}
        lineHighlight={store.lineHighlight}
        top={store.top}
        caseSensitiveSearch={store.caseSensitiveSearch}
        lastSearchFocus={store.lastSearchFocus}
        scrollToTop={store.scrollToTop}
        fontSize={store.fontSize}
        marginLeft={store.marginLeft}
        lineHeight={store.lineHeight}
        setMarginLeft={store.setMarginLeft}
        width={store.width}
        minWidth={store.minWidth}
        minHeight={store.minHeight}
        height={store.height}
        getStringForSearch={store.getStringForSearch}
        setPrevUrlPatterns={store.setPrevUrlPatterns}
        numbersColor={store.numbersColor}
        search={store.search}
        updateUrlBulkInfo={store.updateUrlBulkInfo}
        isValid={store.isValid}
        lastGoToUpdate={store.lastGoToUpdate}
        goTo={store.goTo}
        setWasFocusedInLastDraw={store.setWasFocusedInLastDraw}
        needsWarning={store.needsWarning}
        warningBackground={store.warningBackground}
        numbersWarningColor={store.numbersWarningColor}
        hasWarnings={store.hasWarnings}
        {...props}
    />);

ObservableBulkEditor.propTypes = {
    store: BulkEditorStore.PropType.isRequired,
    ...BulkEditor.propTypes,
};
